import { useIsMutating } from "@tanstack/react-query";
import { useState } from "react";
import {
  HiOutlineArrowDownTray,
  HiOutlineArrowPath,
  HiOutlineSignalSlash,
} from "react-icons/hi2";

import { currentPlatform } from "../web-push";
import { isInstallHintSnoozed, snoozeInstallHint } from "./installHint";
import {
  useConnection,
  useInstall,
  usePwa,
  useServiceWorkerUpdate,
} from "./pwaServices";

/**
 * Сообщения о состоянии приложения: сеть, новая версия, установка.
 *
 * Живут в одной панели поверх страницы, а не в шапке: должны быть видны и на
 * экране входа, и при открытом окне, и не сдвигать вёрстку разделов.
 */
export function PwaNotices({
  onOpenAbout,
  hideInstall = false,
}: {
  onOpenAbout: () => void;
  /** Предложение установки лишнее там, где инструкция уже на экране. */
  hideInstall?: boolean;
}) {
  const { status } = useConnection();
  const update = useServiceWorkerUpdate();
  // Установка — самое необязательное из сообщений: не громоздим его поверх
  // сообщения о сети или новой версии, особенно на экране телефона.
  const quiet = status === "online" && update.status === "idle";

  return (
    <div
      className="pwa-notices"
      role="region"
      aria-label="Состояние приложения"
    >
      <ConnectionNotice />
      <UpdateNotice />
      {quiet && !hideInstall ? (
        <InstallNotice onOpenAbout={onOpenAbout} />
      ) : null}
    </div>
  );
}

function ConnectionNotice() {
  const { connection } = usePwa();
  const { status, restored, rejectedWrite } = useConnection();
  if (status === "online" && !restored && !rejectedWrite) return null;

  const rejected = rejectedWrite ? (
    <p className="pwa-notice__warning" role="alert">
      Последнее действие не выполнено: оно не дошло до сервера.{" "}
      {status === "online"
        ? "Повторите его."
        : "Данные остались в форме — повторите действие, когда связь вернётся."}
    </p>
  ) : null;

  if (status === "offline") {
    return (
      <section className="pwa-notice pwa-notice--offline" role="status">
        <HiOutlineSignalSlash aria-hidden="true" />
        <div>
          <strong>Нет соединения</strong>
          <p>
            Данные могут быть устаревшими. Изменения без связи не сохраняются и
            не ставятся в очередь — повторите их, когда связь вернётся.
          </p>
          {rejected}
        </div>
      </section>
    );
  }

  if (status === "reconnecting") {
    return (
      <section className="pwa-notice pwa-notice--reconnecting" role="status">
        <HiOutlineArrowPath aria-hidden="true" className="pwa-notice__spin" />
        <div>
          <strong>Соединение восстановлено</strong>
          <p>
            Обновляем данные: пользователя, права, уведомления и открытые
            разделы…
          </p>
          {rejected}
        </div>
      </section>
    );
  }

  return (
    <section
      className={`pwa-notice ${restored ? "pwa-notice--online" : "pwa-notice--offline"}`}
      role="status"
    >
      <div>
        <strong>
          {restored ? "Данные обновлены" : "Нет связи с сервером"}
        </strong>
        {restored ? (
          <p>Связь есть, актуальные данные загружены с сервера.</p>
        ) : null}
        {rejected}
      </div>
      {rejectedWrite ? (
        <button
          className="button button--text button--compact"
          type="button"
          onClick={() => connection.dismissRejectedWrite()}
        >
          Понятно
        </button>
      ) : null}
    </section>
  );
}

function UpdateNotice() {
  const { updater, unsaved } = usePwa();
  const { status } = useServiceWorkerUpdate();
  const mutating = useIsMutating();
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (status === "idle" || (status === "available" && dismissed)) return null;

  if (status === "applying") {
    return (
      <section className="pwa-notice" role="status">
        <HiOutlineArrowPath aria-hidden="true" className="pwa-notice__spin" />
        <div>
          <strong>Обновляем приложение…</strong>
          <p>Страница перезагрузится автоматически.</p>
        </div>
      </section>
    );
  }

  const apply = () => {
    if (status === "reload-recommended") updater.reload();
    else updater.apply();
  };
  const request = () => {
    // Перезагрузка стирает ввод, который не отправлен: сначала спрашиваем.
    if (unsaved.hasUnsavedChanges() || mutating > 0) {
      setConfirming(true);
      return;
    }
    apply();
  };
  const heading =
    status === "reload-recommended"
      ? "Приложение обновлено в другой вкладке"
      : "Доступна новая версия ERP";
  const description =
    status === "reload-recommended"
      ? "Эта вкладка работает на старой версии. Перезагрузите её, когда сохраните работу."
      : "Она уже загружена. Мы не обновляем страницу сами, чтобы не потерять то, что вы вводите.";

  return (
    <section className="pwa-notice" role="status">
      <HiOutlineArrowPath aria-hidden="true" />
      <div>
        <strong>{heading}</strong>
        <p>{description}</p>
        {confirming ? (
          <p className="pwa-notice__warning" role="alert">
            {mutating > 0
              ? "Прямо сейчас что-то сохраняется. "
              : "В открытых формах есть данные, которые не отправлены. "}
            После обновления страница перезагрузится, и они пропадут. Сохраните
            их и обновитесь позже — или обновитесь сейчас и введите заново.
          </p>
        ) : null}
        <div className="pwa-notice__actions">
          {confirming ? (
            <>
              <button
                className="button button--secondary button--compact"
                type="button"
                onClick={() => setConfirming(false)}
              >
                Вернуться и сохранить
              </button>
              <button
                className="button button--primary button--compact"
                type="button"
                onClick={apply}
              >
                Обновить и потерять ввод
              </button>
            </>
          ) : (
            <>
              <button
                className="button button--primary button--compact"
                type="button"
                onClick={request}
              >
                {status === "reload-recommended" ? "Перезагрузить" : "Обновить"}
              </button>
              {status === "available" ? (
                <button
                  className="button button--text button--compact"
                  type="button"
                  onClick={() => setDismissed(true)}
                >
                  Позже
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function InstallNotice({ onOpenAbout }: { onOpenAbout: () => void }) {
  const { installer } = usePwa();
  const { standalone, canPrompt, installedNow } = useInstall();
  const [hidden, setHidden] = useState(() => isInstallHintSnoozed());
  const platform = currentPlatform();

  if (standalone || installedNow || hidden) return null;
  // Safari на iPhone не сообщает о возможности установки — подсказываем сами.
  const manualIos = !canPrompt && platform === "ios";
  if (!canPrompt && !manualIos) return null;

  const hide = () => {
    snoozeInstallHint();
    setHidden(true);
  };

  return (
    <section className="pwa-notice" role="status">
      <HiOutlineArrowDownTray aria-hidden="true" />
      <div>
        <strong>Установите ERP как приложение</strong>
        <p>
          {manualIos
            ? "Иконка на экране «Домой», отдельное окно без адресной строки и уведомления при закрытом браузере. В Safari нажмите «Поделиться» → «На экран «Домой»»."
            : "Иконка на рабочем столе, отдельное окно без адресной строки и уведомления при закрытом браузере."}
        </p>
        <div className="pwa-notice__actions">
          {manualIos ? (
            <button
              className="button button--primary button--compact"
              type="button"
              onClick={onOpenAbout}
            >
              Подробнее
            </button>
          ) : (
            <button
              className="button button--primary button--compact"
              type="button"
              onClick={() => {
                void installer.prompt().then((outcome) => {
                  if (outcome === "dismissed") hide();
                });
              }}
            >
              Установить
            </button>
          )}
          <button
            className="button button--text button--compact"
            type="button"
            onClick={hide}
          >
            Не сейчас
          </button>
        </div>
      </div>
    </section>
  );
}
